import { useWebsocketStore } from '@/stores';
import authApi from '@/api/auth';

/*
 * The websocket server only delivers messages for topics a socket subscribed to:
 * - student:<id> (and studio:<url> for teachers), proven with a ticket from GET /api/ws-ticket
 * - event:<id> for the event currently loaded in the event store, which is public
 * Only logged-in users connect. Visitors never see a join button that live updates would reveal,
 * and the event page's countdown already refetches at start time.
 */

const RECONNECT_MIN_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
// refetch a ticket that expires within this window instead of reusing it on reconnect
const TICKET_REFRESH_MARGIN_S = 60;

let socket: WebSocket | null = null;
let wanted = false;
let ticket: WebsocketTicket | null = null;
let eventTopic: string | null = null;
let reconnectDelay = RECONNECT_MIN_DELAY_MS;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function listener(event: MessageEvent) {
  if (!event.data) return;
  const { event_type, payload } = JSON.parse(event.data);
  if (!event_type || event_type === 'ws.subscribed') return;

  const store = useWebsocketStore();
  store.eventReceived({ event_type, payload });
}

function send(message: object) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
}

function subscribeAll() {
  const topics = [...(ticket?.topics || [])];
  if (eventTopic) topics.push(eventTopic);
  if (!topics.length) return;
  send({ action: 'subscribe', topics, ticket: ticket?.ticket });
}

async function refreshTicket() {
  try {
    ticket = await authApi.getWebsocketTicket();
  } catch (error) {
    // the socket still works for public event topics without one
    ticket = null;
    console.error('Could not load websocket ticket', error);
  }
}

function ticketIsFresh() {
  return !!ticket && ticket.expires - TICKET_REFRESH_MARGIN_S > Date.now() / 1000;
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket();
  }, reconnectDelay);
  reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_DELAY_MS);
}

async function openSocket() {
  if (!wanted || socket) return;
  const wsUrl = process.env.VUE_APP_WEBSOCKET_URL;
  if (!wsUrl) {
    console.error('Websocket URL is not defined');
    return;
  }
  if (!ticketIsFresh()) await refreshTicket();
  // logged out, or another call opened one, while the ticket was loading
  if (!wanted || socket) return;

  const ws = new WebSocket(wsUrl);
  socket = ws;
  ws.addEventListener('message', listener);
  ws.addEventListener('open', () => {
    reconnectDelay = RECONNECT_MIN_DELAY_MS;
    subscribeAll();
  });
  ws.addEventListener('close', () => {
    if (socket !== ws) return;
    socket = null;
    // Heroku restarts the dyno daily; without this, open tabs would silently stop getting events
    if (wanted) scheduleReconnect();
  });
}

// Call when a user logs in or switches account; safe to call repeatedly.
export async function connectSocket() {
  wanted = true;
  if (socket) {
    const previousTopics = ticket?.topics || [];
    await refreshTicket();
    const revoked = previousTopics.filter(topic => !ticket?.topics.includes(topic));
    if (revoked.length) send({ action: 'unsubscribe', topics: revoked });
    subscribeAll();
    return;
  }
  await openSocket();
}

export function disconnectSocket() {
  wanted = false;
  ticket = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    const ws = socket;
    socket = null;
    ws.close();
  }
}

// Follow live updates for the event the user is looking at (null to stop).
export function setSocketEvent(eventId: number | null | undefined) {
  const topic = eventId ? `event:${eventId}` : null;
  if (topic === eventTopic) return;
  if (eventTopic) send({ action: 'unsubscribe', topics: [eventTopic] });
  eventTopic = topic;
  if (eventTopic) send({ action: 'subscribe', topics: [eventTopic] });
}
